import { useState } from 'react'

import { fecha as formatoFecha, pesos } from '@/shared/utilidades/formato'

import { useCotizacionesParaPartir, useTraerRenglones } from './usePartirDe'
import { VistaPreviaAnterior } from './VistaPreviaAnterior'
import estilos from './PartirDeAnterior.module.css'

type Props = {
  ordenServicioId: string
  cotizacionId: string
  /** Se avisa para que el cotizador recargue los renglones que acaban de entrar. */
  onTraido: () => void
}

/** Cuánto hace, en palabras. La fecha exacta va al lado; esto es para ubicarse. */
function haceCuanto(fechaIso: string): string {
  const dias = Math.floor((Date.now() - new Date(fechaIso).getTime()) / 86_400_000)

  if (dias <= 0) return 'hoy'
  if (dias === 1) return 'ayer'
  if (dias < 30) return `hace ${dias} días`
  if (dias < 60) return 'hace un mes'
  if (dias < 365) return `hace ${Math.floor(dias / 30)} meses`
  return 'hace más de un año'
}

/**
 * Partir de una cotización anterior.
 *
 * La cotización típica lleva seis renglones y 861 del histórico llevan diez o
 * más. Buscarlos uno por uno son varios minutos y el riesgo de olvidar alguno;
 * traer un trabajo parecido y ajustar cantidades son segundos.
 *
 * No se duplica: se AGREGA sobre lo que haya, y con los precios de hoy.
 */
export function PartirDeAnterior({ ordenServicioId, cotizacionId, onTraido }: Props) {
  const { anteriores, cargando } = useCotizacionesParaPartir(ordenServicioId)
  const traer = useTraerRenglones()
  const [abierta, setAbierta] = useState<number | null>(null)
  const [trayendo, setTrayendo] = useState<number | null>(null)
  const [fallo, setFallo] = useState<string | null>(null)

  if (cargando || anteriores.length === 0) return null

  async function traerDe(idAsiento: number) {
    setTrayendo(idAsiento)
    setFallo(null)

    try {
      await traer.mutateAsync({ cotizacionId, idAsiento })
      onTraido()
    } catch (error) {
      setFallo(error instanceof Error ? error.message : 'No fue posible traer los renglones.')
    } finally {
      setTrayendo(null)
    }
  }

  return (
    <section className={estilos.zona}>
      <p className={estilos.encabezado}>Partir de una anterior</p>

      <div className={estilos.lista}>
        {anteriores.map((anterior) => {
          const estaAbierta = abierta === anterior.id_asiento

          return (
            <article
              key={anterior.id_asiento}
              className={`${estilos.tarjeta} ${estaAbierta ? estilos.abierta : ''}`}
            >
              <div className={estilos.encima}>
                <span className={estilos.numero}>{anterior.numero_visible}</span>
                <span className={`${estilos.motivo} ${estilos[`m${anterior.relevancia}`]}`}>
                  {anterior.motivo}
                </span>
              </div>

              {/* La fecha manda: los precios y el alcance de un trabajo de hace
                  ocho meses no son los de uno de la semana pasada. */}
              <div className={estilos.cuando}>
                <span className={estilos.fecha}>{formatoFecha(anterior.fecha)}</span>
                <span className={estilos.hace}>{haceCuanto(anterior.fecha)}</span>
              </div>

              <p className={estilos.cliente}>{anterior.cliente_nombre ?? 'Sin cliente'}</p>

              <div className={estilos.abajo}>
                <span className={estilos.renglones}>
                  {anterior.renglones} {anterior.renglones === 1 ? 'concepto' : 'conceptos'}
                </span>
                <span className={estilos.total}>{pesos(anterior.total)}</span>
              </div>

              {estaAbierta && <VistaPreviaAnterior idAsiento={anterior.id_asiento} />}

              <div className={estilos.acciones}>
                <button
                  type="button"
                  className={estilos.ver}
                  onClick={() => setAbierta(estaAbierta ? null : anterior.id_asiento)}
                >
                  {estaAbierta ? 'Cerrar' : 'Ver qué lleva'}
                </button>

                <button
                  type="button"
                  className={estilos.traerla}
                  onClick={() => void traerDe(anterior.id_asiento)}
                  disabled={trayendo !== null}
                >
                  {trayendo === anterior.id_asiento ? 'Trayendo…' : 'Traer'}
                </button>
              </div>
            </article>
          )
        })}
      </div>

      {fallo ? (
        <p className={estilos.fallo}>{fallo}</p>
      ) : (
        <p className={estilos.nota}>
          Se agregan sus conceptos con los precios de hoy. Después se ajustan las cantidades.
        </p>
      )}
    </section>
  )
}
